import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import { handleApiError } from "@/utils/apiErrorHandler";
import { clearError } from "./errorSlice";

export const fetchAboutSectionByName = createAsyncThunk(
  "aboutPremium/fetchAboutSectionByName",
  async (sectionName: string, { dispatch, rejectWithValue }) => {
    try {
      // Clear any previous errors from this slice when starting a new request
      dispatch(clearError());
      const response = await axios.get(
        `/papi/content/about-sections/by-name?sectionName=${sectionName}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError, "aboutPremium", dispatch);
      return rejectWithValue(error instanceof Error ? error.message : "Failed to fetch About Section By Name");
    }
  }
);

// Working - This thunk fetches the about menu sections and their children
export const fetchAboutMenus = createAsyncThunk(
  "aboutPremium/fetchAboutMenu",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      dispatch(clearError());
      const response = await axios.get(
        `/papi/content/about-sections/with-children?sort=id,asc`
      );
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError, "aboutPremium", dispatch);
      return rejectWithValue(error instanceof Error ? error.message : "Failed to fetch About Menu");
    }
  }
);

type SectionsType = {
  id: number;
  header: string;
  name: string;
  priority: number;
  content: string;
  parentId: number | null;
  parentHeader: string | null;
};

type MenusType = {
  id: number;
  header: string;
  name: string;
  parentHeader: string | null;
  parentId: number | null;
  priority: number;
  children: Array<SectionsType> | null;
};

interface AboutPremiumState {
  sections: SectionsType | null;
  menus: MenusType[] | null;
  menusLoading: boolean;
  sectionsLoading: boolean;
  error: string | null;
}

const initialState: AboutPremiumState = {
  sections: null,
  menus: null,
  menusLoading: false,
  sectionsLoading: false,
  error: null,
};

const aboutPremiumSlice = createSlice({
  name: "aboutPremium",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAboutSectionByName.fulfilled, (state, action) => {
        state.sections = action.payload.data;
        state.sectionsLoading = false;
      })
      .addCase(fetchAboutSectionByName.pending, (state) => {
        state.sectionsLoading = true;
      })
      .addCase(fetchAboutSectionByName.rejected, (state, action) => {
        state.error = action.error.message ?? null;
        state.sectionsLoading = false;
      })
      .addCase(fetchAboutMenus.fulfilled, (state, action) => {
        state.menus = action.payload.data;
        state.menusLoading = false;
      })
      .addCase(fetchAboutMenus.pending, (state) => {
        state.menusLoading = true;
      })
      .addCase(fetchAboutMenus.rejected, (state, action) => {
        state.error = action.error.message ?? null;
        state.menusLoading = false;
      });
  },
});

export default aboutPremiumSlice.reducer;
